/**
 * Shared types for chunk upload functionality
 */

export const DEFAULT_CHUNK_SIZE = 2 * 1024 * 1024 // 2MB
export const DEFAULT_RETRY_DELAYS = [2000, 4000, 8000] // 2s, 4s, 8s (legacy, kept for compatibility)
export const DEFAULT_RETRY_TIMES = 3 // legacy, kept for compatibility
export const MAX_BLKWRITTEN_RETRIES = 3 // legacy, kept for compatibility
export const INITIAL_RETRY_DELAY = 2000 // 2 seconds initial delay for exponential backoff
export const MAX_RETRY_DELAY = 60000 // 60 seconds maximum delay

/**
 * Calculate retry delay using exponential backoff
 * delay = min(INITIAL_RETRY_DELAY * 2^attempt, MAX_RETRY_DELAY)
 * @param attempt The current retry attempt (0-indexed)
 * @returns Delay in milliseconds
 */
export function calculateRetryDelay(attempt: number): number {
  const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt)
  return Math.min(delay, MAX_RETRY_DELAY)
}

/**
 * Standard response format for chunk upload APIs
 */
export interface ChunkUploadResponse {
  code?: number
  info?: {
    blkWritten?: Record<string, boolean>
  }
}

/**
 * Configuration options for BaseChunkUploader
 */
export interface BaseChunkUploaderOptions {
  chunkSize?: number
  concurrency?: number
  retryTimes?: number
  retryDelays?: number[]
}

/**
 * Error thrown when upload is paused by user
 */
export class UploadPausedError extends Error {
  constructor() {
    super('Upload paused by user')
    this.name = 'UploadPausedError'
  }
}

/**
 * Type for the upload function passed to uploadSingleChunkWithRetry
 */
export type ChunkUploadFn<TContext, TResponse extends ChunkUploadResponse> = (
  chunk: Blob,
  index: number,
  context: TContext,
  signal: AbortSignal,
) => Promise<TResponse>
